import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AlertCircle, BarChart3, CheckCircle, Lock, Search } from 'lucide-react'
import { lumi } from '../lib/lumi'
import { stripHtml } from '../lib/surveyPresentation'
import { verifyClientPortalPassword } from '../lib/portalAccess'
import type { Question } from '../components/QuestionBuilder'

interface Survey {
  _id: string
  title: string
  description?: string
  questions?: Question[]
  clientName?: string
  clientResultsEnabled?: boolean
  clientResultsToken?: string
  clientResultsPassword?: string
  clientResultsPasswordHash?: string
  clientResultsPasswordSalt?: string
  clientResultsPasswordUpdatedAt?: string
}

interface SurveyResponse {
  _id: string
  surveyId?: string
  status?: string
  submittedAt?: string
  completedAt?: string
  isDeleted?: boolean
  deletedAt?: string
  answers?: Array<{
    questionId: string
    answer: any
  }>
}

interface QuestionResult {
  questionId: string
  title: string
  type: string
  total: number
  counts: Array<{
    label: string
    count: number
    percent: number
  }>
}

const toDisplayText = (value: any, question?: Question): string[] => {
  if (value === undefined || value === null || value === '') return []

  const optionLabel = (rawValue: any) => {
    const normalized = String(rawValue)
    const option = question?.options?.find((item) => (
      String(item.id) === normalized || String((item as any).value ?? '') === normalized
    ))
    return option ? stripHtml(option.text) : normalized
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => toDisplayText(item, question))
  }

  if (typeof value === 'object') {
    if (Array.isArray(value.values)) {
      const labels = value.values.flatMap((item: any) => toDisplayText(item, question))
      if (value.otherText) labels.push(`Outro: ${String(value.otherText)}`)
      return labels
    }

    return Object.entries(value)
      .filter(([, itemValue]) => itemValue !== undefined && itemValue !== null && itemValue !== '')
      .map(([key, itemValue]) => `${key}: ${optionLabel(itemValue)}`)
  }

  return [optionLabel(value)]
}

const buildQuestionResults = (survey: Survey, responses: SurveyResponse[]): QuestionResult[] => {
  const questions = survey.questions || []
  const questionMap = new Map(questions.map((question) => [question.id, question]))
  const counters = new Map<string, Map<string, number>>()
  const totals = new Map<string, number>()

  responses.forEach((response) => {
    ;(response.answers || []).forEach((answerRecord) => {
      const question = questionMap.get(answerRecord.questionId)
      if (!question) return

      const labels = toDisplayText(answerRecord.answer, question)
      if (labels.length === 0) return

      totals.set(question.id, (totals.get(question.id) || 0) + 1)

      if (!counters.has(question.id)) counters.set(question.id, new Map())
      const questionCounter = counters.get(question.id)!

      labels.forEach((label) => {
        questionCounter.set(label, (questionCounter.get(label) || 0) + 1)
      })
    })
  })

  return questions.map((question) => {
    const total = totals.get(question.id) || 0
    const questionCounter = counters.get(question.id) || new Map<string, number>()
    const counts = Array.from(questionCounter.entries())
      .map(([label, count]) => ({
        label,
        count,
        percent: total > 0 ? Math.round((count / total) * 100) : 0
      }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))

    return {
      questionId: question.id,
      title: stripHtml(question.title || 'Pergunta sem título'),
      type: question.type,
      total,
      counts
    }
  })
}

const ClientResultsPortal: React.FC = () => {
  const { token = '' } = useParams()
  const [password, setPassword] = useState('')
  const [survey, setSurvey] = useState<Survey | null>(null)
  const [responses, setResponses] = useState<SurveyResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [authorized, setAuthorized] = useState(false)

  const questionResults = useMemo(
    () => survey ? buildQuestionResults(survey, responses) : [],
    [responses, survey]
  )

  const handleAccess = async (event: React.FormEvent) => {
    event.preventDefault()
    setError('')
    setLoading(true)

    try {
      const surveyResponse = await lumi.entities.surveys.list({
        filter: {
          clientResultsToken: token,
          clientResultsEnabled: true
        },
        limit: 1
      }) as { list?: Survey[] }

      const foundSurvey = surveyResponse.list?.[0]
      if (!foundSurvey) {
        setError('Link de resultados não encontrado ou desativado.')
        return
      }

      if (!(await verifyClientPortalPassword(password, foundSurvey))) {
        setError('Senha incorreta.')
        return
      }

      const responseList = await lumi.entities.responses.list({
        filter: {
          surveyId: foundSurvey._id
        },
        limit: 5000
      }) as { list?: SurveyResponse[] }

      setSurvey(foundSurvey)
      setResponses((responseList.list || []).filter((item) => (
        !item.isDeleted &&
        !item.deletedAt &&
        (item.status === 'completed' || Boolean(item.submittedAt || item.completedAt))
      )))
      setAuthorized(true)
    } catch (loadError) {
      console.error('Erro ao carregar portal do cliente:', loadError)
      setError('Não foi possível carregar os resultados.')
    } finally {
      setLoading(false)
    }
  }

  if (!authorized || !survey) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10">
        <form onSubmit={handleAccess} className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-6 flex items-center gap-3">
            <div className="rounded-lg bg-blue-100 p-3 text-blue-700">
              <Lock size={24} />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Portal de resultados</h1>
              <p className="mt-1 text-sm text-slate-600">Informe a senha enviada para o cliente.</p>
            </div>
          </div>

          <label className="mb-2 block text-sm font-medium text-slate-700">
            Senha
          </label>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="mb-4 w-full rounded-lg border border-slate-300 px-4 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />

          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle className="mt-0.5 shrink-0" size={16} />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !password.trim()}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Search size={18} />
            {loading ? 'Carregando...' : 'Acessar resultados'}
          </button>
        </form>
      </main>
    )
  }

  return (
    <main className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-6 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-medium uppercase tracking-wide text-blue-700">Resultados do cliente</p>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">{stripHtml(survey.title)}</h1>
            {survey.clientName && (
              <p className="mt-1 text-sm font-medium text-slate-700">Cliente: {survey.clientName}</p>
            )}
            {survey.description && (
              <p className="mt-2 max-w-3xl text-sm text-slate-600">{stripHtml(survey.description)}</p>
            )}
          </div>
          <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-blue-900">
            <p className="text-xs font-medium uppercase tracking-wide">Respostas</p>
            <p className="text-2xl font-semibold">{responses.length}</p>
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-4 py-6">
        {questionResults.length === 0 ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-500">
            Nenhum resultado disponível para esta pesquisa.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {questionResults.map((result) => (
              <article key={result.questionId} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-4 flex items-start justify-between gap-4">
                  <div>
                    <h2 className="break-words text-base font-semibold text-slate-900">{result.title}</h2>
                    <p className="mt-1 text-xs uppercase tracking-wide text-slate-500">{result.type}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                    <BarChart3 size={14} />
                    {result.total}
                  </div>
                </div>

                {result.counts.length === 0 ? (
                  <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-500">Sem respostas contabilizadas.</p>
                ) : (
                  <div className="space-y-3">
                    {result.counts.map((item) => (
                      <div key={item.label}>
                        <div className="mb-1 flex items-center justify-between gap-3 text-sm">
                          <span className="break-words text-slate-700">{item.label}</span>
                          <span className="shrink-0 font-medium text-slate-900">{item.count} ({item.percent}%)</span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full rounded-full bg-blue-600" style={{ width: `${item.percent}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}

        <div className="mt-6 flex items-center gap-2 text-sm text-slate-500">
          <CheckCircle size={16} className="text-emerald-600" />
          Dados atualizados no momento do acesso ao link.
        </div>
      </section>
    </main>
  )
}

export default ClientResultsPortal
